// topoLiveEditor/topoLiveEditor.js — createTopoLiveEditor: a minimal contenteditable WYSIWYG editor.
//
// THE STANDARD for text fields users see (overlay info_text, the message center texts; rules/javascript_vite.md).
// topoEditor (plain text + preview) stays as a development view that shows what the convention turns into.
//
// The counterpart to topoEditor (TopoPreviewEditor). Where TopoPreviewEditor authors the value as plain
// text + convention and PARSES it to a rich preview, TopoLiveEditor edits rich content IN PLACE and
// SERIALIZES the DOM back to the SAME plain-text convention — everything routed through the hub (topoDoc):
//
//   • paint:  convention --parseTokensFromConvention--> tokens --renderLiveHtmlFromTokens--> contenteditable
//   • commit: contenteditable --parseTokensFromEditorHtml--> tokens --renderConventionFromTokens--> convention
//
// ENTITY-DRIVEN: each entity owns its live form (renderLiveHtml), its recognition (matchElement) and its
// authoring (edit: fields/canCreate/newData/normalize/silentCreate). The editor knows no entity specifics.
//   • selection toolbar: one square button per entity whose edit.canCreate(selection) is true (Link = any
//     non-empty text; Doc = only a numeric selection). Click wraps/inserts the entity, then opens the popover
//     (unless edit.silentCreate).
//   • popover: panel-style — accent titlebar (title + close X); field-driven body per entity.edit.fields; actions = OK (save) + trash (remove).
//   • close: clicking plain editor area or the same token again closes the popover.
//   • paste: plain text only (rich text would show its formatting until blur, but only the text is stored); files on
//     the clipboard are left to the consumer first (message center: the attachment picker on the form).
//   • { raw: true }: an eye checkbox below the field shows the stored value (the convention text) read only; it starts
//     off. Later a debug tool with its own permission (plan jsplugin_topoeditor, phase raw-debug).
//
// Storage stays the convention string (Option 2); getRawHtml() exposes the innerHTML (HTML-storage study view).
// Factory form + DisposerBag teardown, per rules/javascript_vite.md.

import { createDisposers } from "../core/disposers.js";
import { insertPlainText, pastedText } from "../core/plainTextPaste.js";
import {
    parseTokensFromConvention,
    parseTokensFromEditorHtml,
    renderLiveHtmlFromTokens,
    renderConventionFromTokens,
} from "../topoEditor/topoDoc.js";
import { resolveDocs } from "../topoEditor/docResolve.js";
import { escapeHtml } from "../topoEditor/render.js";

// Remove-button glyph (an X), stroke:currentColor like the OverlayToolbar SVGs.
const CLOSE_ICON = '<path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>';

// German fallbacks only — the consumer passes translated labels (infoTextEditor.js).
const DEFAULT_LABELS = {
    close:       "Schließen",
    save:        "Speichern",
    ok:          "OK",
    remove:      "Entfernen",
    edit:        "Bearbeiten",
    document:    "Dokument",
    unpublished: "nicht freigegeben",
    raw:         "Raw",
};

function mk(tag, cls) {
    const el = document.createElement(tag);
    if (cls) { el.className = cls; }
    return el;
}

function fromHtml(html) {
    const d = document.createElement("div");
    d.innerHTML = html;
    return d.firstChild;
}

export function createTopoLiveEditor({ value = "", entities = [], placeholder = "", rows = 3, raw = false, onChange, labels = {} } = {}) {
    const text = { ...DEFAULT_LABELS, ...labels };
    const bag = createDisposers();
    let val = value;
    const byKind = new Map(entities.map((e) => [e.kind, e]));
    const creatable = entities.filter((e) => e.edit && typeof e.edit.canCreate === "function" && typeof e.edit.newData === "function");
    const editableByKind = new Map(entities.filter((e) => e.edit && Array.isArray(e.edit.fields)).map((e) => [e.kind, e]));

    const root = mk("div", "topo2-live-editor");
    root.style.setProperty("--te-rows", rows);
    const surface = mk("div", "topo2-editor-surface");
    const view = mk("div", "topo2-editor-display topo2-live-surface");
    view.contentEditable = "true";
    view.setAttribute("role", "textbox");
    view.setAttribute("aria-multiline", "true");
    if (placeholder) { view.dataset.placeholder = placeholder; }
    surface.appendChild(view);
    root.appendChild(surface);

    function on(node, ev, fn) {
        node.addEventListener(ev, fn);
        bag.add(() => node.removeEventListener(ev, fn));
    }

    // ── raw switch (opt-in): [ eye | "Raw" ] below the field; on, a read-only box shows the stored value ──
    let rawBox = null;
    if (raw) {
        const rawRow = mk("div", "topo2-live-raw");
        const toggle = mk("label", "topo2-editor-eye-toggle");
        const check = mk("input");
        check.type = "checkbox";
        const caption = mk("span");
        caption.textContent = text.raw;
        toggle.append(check, mk("i", "topo2-eye"), caption);
        rawBox = mk("pre", "topo2-live-raw-box");
        rawBox.hidden = true;
        rawRow.append(toggle, rawBox);
        root.appendChild(rawRow);
        on(check, "change", () => { rawBox.hidden = !check.checked; });
    }
    function showRaw() { if (rawBox) { rawBox.textContent = val; } }

    const ZWSP = String.fromCharCode(0x200b);   // zero-width space (U+200B)
    // contenteditable can't place the caret AFTER a trailing inline/atomic element (icon-only link, doc/overlay
    // token) that is the last child → you couldn't type after it. Keep a zero-width-space text node as the last
    // child so there is a caret slot on the same line. It never reaches the value (commitFromDom strips ZWSP).
    function ensureTrailingCaret() {
        const last = view.lastChild;
        if (last && last.nodeType === 1) { view.appendChild(document.createTextNode(ZWSP)); }
    }

    function paint() {
        view.innerHTML = renderLiveHtmlFromTokens(parseTokensFromConvention(val, entities), entities);
        view.classList.toggle("is-empty", val.trim() === "");
        decorateDocLinks();
        ensureTrailingCaret();
        showRaw();
    }
    // Doc-Links ({id}) im Editor auflösen: nicht existent ODER unpublished → durchgestrichen (.is-unpublished)
    // + Tooltip „Dokument <id> - nicht freigegeben". Async/best-effort (bei Netzfehler bleibt der Default-Zustand).
    function decorateDocLinks() {
        const anchors = Array.prototype.slice.call(view.querySelectorAll('.topo2-live-doclink[data-id]'));
        if (!anchors.length) { return; }
        resolveDocs(anchors.map((a) => a.getAttribute("data-id"))).then((map) => {
            anchors.forEach((a) => {
                const id = a.getAttribute("data-id");
                const r = map[id];
                if (!r) { return; }
                const bad = !r.exists || !r.published;
                a.classList.toggle("is-unpublished", bad);
                a.title = text.document + " " + id + (bad ? " - " + text.unpublished : "");
            });
        });
    }
    function commitFromDom() {
        // strip the trailing-caret filler (ensureTrailingCaret) so the ZWSP never lands in the stored value
        val = renderConventionFromTokens(parseTokensFromEditorHtml(view, entities), entities).split(ZWSP).join("");
        view.classList.toggle("is-empty", val.trim() === "");
        showRaw();
        onChange && onChange(val);
    }

    on(view, "input", commitFromDom);
    // Paste as plain text. Handled on the document, after the event has passed the consumer's listeners: one of them
    // may take files from the clipboard (attachment picker) and prevent the default; then the editor stays out.
    on(document, "paste", (e) => {
        if (e.defaultPrevented || !view.contains(e.target) || !e.clipboardData) { return; }
        e.preventDefault();
        insertPlainText(view, pastedText(e));   // ends with an "input" event → commitFromDom
    });
    // Re-detect convention-only entities (e.g. a typed "-word" hidden keyword) once focus LEAVES the editor —
    // a repaint would move the caret, so only do it when not staying inside the editor (popover/toolbar).
    on(view, "blur", (e) => {
        if (root.contains(e.relatedTarget)) { return; }
        commitFromDom();
        paint();
    });

    // ── selection toolbar (square buttons, one per creatable entity, shown by canCreate) ──
    let bar = null;
    const barButtons = [];
    function ensureBar() {
        if (bar) { return bar; }
        bar = mk("div", "topo2-editor-linkbar");
        bar.style.display = "none";   // NOT the [hidden] attr — the linkbar's CSS display:flex would override it
        creatable.forEach((entity) => {
            const isIcon = !!entity.edit.icon;
            // --icon = square SVG button (Link); --bar = fixed-height text button, width grows with the label (Doc).
            const b = mk("button", "topo2-btn " + (isIcon ? "topo2-btn--icon" : "topo2-btn--bar") +
                " topo2-btn--small topo2-btn--outline topo2-editor-linkbar-btn");
            b.type = "button";
            b.title = entity.edit.title || entity.edit.label || "";
            if (isIcon) { b.innerHTML = '<svg viewBox="0 0 24 24">' + entity.edit.icon + "</svg>"; }
            else { b.textContent = entity.edit.label || "?"; }
            // preventDefault keeps the selection alive; stopPropagation keeps the outside-click handler from closing us.
            b.addEventListener("mousedown", (ev) => { ev.preventDefault(); ev.stopPropagation(); createFromSelection(entity); });
            barButtons.push({ entity, b });
            bar.appendChild(b);
        });
        surface.appendChild(bar);
        return bar;
    }
    function hideBar() { if (bar) { bar.style.display = "none"; } }
    function showBarForSelection() {
        const sel = window.getSelection();
        if (!creatable.length || !sel || sel.isCollapsed || sel.rangeCount === 0) { hideBar(); return; }
        const range = sel.getRangeAt(0);
        const text = range.toString();
        if (!text || !view.contains(range.commonAncestorContainer)) { hideBar(); return; }
        if (range.startContainer.parentElement && range.startContainer.parentElement.closest("[data-kind]")) { hideBar(); return; }
        ensureBar();
        // Per-button visibility via style.display (NOT [hidden] — overridden by the button's display:inline-flex).
        let any = false;
        barButtons.forEach(({ entity, b }) => {
            const ok = entity.edit.canCreate(text);
            b.style.display = ok ? "inline-flex" : "none";
            any = any || ok;
        });
        if (!any) { hideBar(); return; }
        bar.style.display = "flex";
        // Sit ABOVE the selection (pingpoli-style); fall back to below if there is no room above.
        const rr = range.getBoundingClientRect();
        const sr = surface.getBoundingClientRect();
        const barH = bar.offsetHeight || 30;
        let top = rr.top - sr.top - barH - 6;
        if (top < 0) { top = rr.bottom - sr.top + 6; }
        bar.style.top = top + "px";
        bar.style.left = Math.max(0, Math.min(rr.left - sr.left, surface.clientWidth - bar.offsetWidth)) + "px";
    }
    on(view, "mouseup", () => setTimeout(showBarForSelection, 0));
    on(view, "keyup", showBarForSelection);
    on(view, "mousedown", hideBar);

    function createFromSelection(entity) {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) { return; }
        const range = sel.getRangeAt(0);
        const text = range.toString();
        if (!text || !view.contains(range.commonAncestorContainer)) { hideBar(); return; }
        const el = fromHtml(entity.renderLiveHtml(entity.edit.newData(text)));
        range.deleteContents();
        range.insertNode(el);
        sel.removeAllRanges();
        hideBar();
        commitFromDom();
        decorateDocLinks();
        ensureTrailingCaret();
        if (!entity.edit.silentCreate) { showPop(entity, el, true); }   // e.g. link → ask for href
    }

    // ── edit popover (panel-style: accent titlebar + close X; field body; OK + trash in the actions) ──
    let pop = null, active = null;

    // Markiert den gerade bearbeiteten Link, solange sein Popover offen ist (User 2026-09-23): ohne das ist bei
    // mehreren Links im Text nicht zu sehen, welcher am Popover hängt. EINE Stelle für Setzen und Entfernen,
    // damit auch der direkte Wechsel von Link zu Link sauber umhängt — showPop wird dabei ohne hidePop gerufen.
    const EDITING_CLASS = "is-editing";
    function setEditingElement(el) {
        if (active && active.el && active.el !== el) { active.el.classList.remove(EDITING_CLASS); }
        if (el) { el.classList.add(EDITING_CLASS); }
    }

    function ensurePop() {
        if (pop) { return pop; }
        pop = mk("div", "topo2-editor-pop");
        pop.hidden = true;
        pop.innerHTML =
            '<div class="topo2-editor-pop-header">' +
                '<span class="topo2-editor-pop-title"></span>' +
                '<button type="button" class="topo2-btn topo2-btn--icon topo2-btn--extrasmall topo2-editor-pop-close" data-a="cancel"><svg viewBox="0 0 24 24">' + CLOSE_ICON + "</svg></button>" +
            "</div>" +
            '<div class="topo2-editor-pop-body">' +
                '<div class="topo2-editor-pop-fields"></div>' +
                '<div class="topo2-editor-pop-picker" hidden></div>' +
                '<div class="topo2-editor-pop-actions">' +
                    '<button type="button" class="topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--outline" data-a="save"></button>' +
                    '<button type="button" class="topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--outline" data-a="remove"><span class="topo2-garbage"></span></button>' +
                "</div>" +
            "</div>";
        surface.appendChild(pop);
        // Texts set via the DOM, not spliced into the HTML above — a translation may contain quotes or "<".
        pop.querySelector('[data-a="cancel"]').title = text.close;
        pop.querySelector('[data-a="save"]').title = text.save;
        pop.querySelector('[data-a="save"]').textContent = text.ok;
        pop.querySelector('[data-a="remove"]').title = text.remove;
        on(pop, "click", (e) => {
            const a = e.target.closest("[data-a]");
            if (!a) { return; }
            if (a.dataset.a === "save") { savePop(); }
            else if (a.dataset.a === "remove") { removePop(); }
            else { hidePop(); }
        });
        return pop;
    }
    function showPop(entity, el, focusLast) {
        ensurePop();
        setEditingElement(el);
        active = { entity, el };
        const data = entity.matchElement(el) || {};
        pop.querySelector(".topo2-editor-pop-title").textContent = entity.edit.title || text.edit;
        pop.querySelector(".topo2-editor-pop-fields").innerHTML = entity.edit.fields.map((f) =>
            // topo2-field-stack: Label über dem Feld, beide linksbündig zur selben Kante (s. fields.css).
            '<label class="topo2-editor-field topo2-field-stack"><span class="topo2-field-label">' + escapeHtml(f.label) +
            '</span><input class="topo2-field-textinput" type="text" data-k="' + f.key + '"></label>'
        ).join("");
        entity.edit.fields.forEach((f) => { pop.querySelector('[data-k="' + f.key + '"]').value = data[f.key] ?? ""; });
        // Optional per-entity picker (e.g. the overlay list) rendered below the fields; selecting a row fills a
        // field via applyValues. Guarded by entity.edit.picker → entities without one (link, docLink) are untouched.
        const pickerBox = pop.querySelector(".topo2-editor-pop-picker");
        pickerBox.innerHTML = "";
        if (entity.edit.picker) {
            const applyValues = (obj) => Object.keys(obj).forEach((k) => {
                const inp = pop.querySelector('[data-k="' + k + '"]');
                if (inp) { inp.value = obj[k]; }
            });
            pickerBox.appendChild(entity.edit.picker(applyValues));
            pickerBox.hidden = false;
        } else {
            pickerBox.hidden = true;
        }
        pop.hidden = false;
        pop.style.top = (el.offsetTop + el.offsetHeight + 7) + "px";
        pop.style.left = Math.max(0, Math.min(el.offsetLeft, surface.clientWidth - 226)) + "px";
        const fields = entity.edit.fields;
        const target = fields[focusLast ? fields.length - 1 : 0];
        const input = target && pop.querySelector('[data-k="' + target.key + '"]');
        if (input) { input.focus(); input.select(); }
    }
    function savePop() {
        if (!active) { return; }
        const values = {};
        active.entity.edit.fields.forEach((f) => { values[f.key] = pop.querySelector('[data-k="' + f.key + '"]').value; });
        const data = active.entity.edit.normalize ? active.entity.edit.normalize(values) : values;
        const sel = window.getSelection();
        if (sel) { sel.removeAllRanges(); }   // same guard as removePop: don't mutate under a selected CE=false token
        active.el.replaceWith(fromHtml(active.entity.renderLiveHtml(data)));
        commitFromDom();
        decorateDocLinks();
        ensureTrailingCaret();
        hidePop();
    }
    function removePop() {
        if (!active) { return; }
        const { entity, el } = active;
        const data = entity.matchElement(el) || {};
        // Clear any selection FIRST: a contenteditable=false token (doc link) is SELECTED as a whole on click, and
        // mutating the DOM while that non-collapsed selection spans it makes the browser delete adjacent text.
        const sel = window.getSelection();
        if (sel) { sel.removeAllRanges(); }
        // Uniform removal: replace with a text node (entity.remove's text, or empty → normalized away = fully removed).
        const text = (typeof entity.edit.remove === "function") ? entity.edit.remove(data) : "";
        el.replaceWith(document.createTextNode(text));
        view.normalize();
        commitFromDom();
        hidePop();
    }
    function hidePop() {
        if (pop) { pop.hidden = true; }
        setEditingElement(null);
        active = null;
    }

    // Click a token → edit it (toggle closed if it is the one already open); click the plain area → close.
    on(view, "click", (e) => {
        const el = e.target.closest("[data-kind]");
        const entity = el && editableByKind.get(el.dataset.kind);
        if (!entity) { hidePop(); return; }
        e.preventDefault();
        if (active && active.el === el) { hidePop(); return; }
        showPop(entity, el, false);
    });
    on(document, "mousedown", (e) => {
        if (pop && !pop.hidden && !pop.contains(e.target) && !view.contains(e.target)) { hidePop(); }
        if (bar && bar.style.display !== "none" && !bar.contains(e.target)) { hideBar(); }
    });

    paint();

    return {
        element: root,
        getValue: () => val,
        getRawHtml: () => view.innerHTML,   // HTML-storage study view (playground only)
        setValue(next) { val = next ?? ""; paint(); onChange && onChange(val); },
        focus: () => view.focus(),
        destroy() { bag.dispose(); root.remove(); },
    };
}
